import { useParams } from 'react-router-dom';
import { useRoleGet } from '../hooks/use-role.hook';
import { memo, useMemo } from 'react';
import RoleDetailsComponent from '../components/role-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { Role } from '../types/role.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@utils/date.util';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import TrailDetailsContainer from '../../trails/containers/trail-details.container';
import PageTitle from '@components/common/PageTitle';

const RoleDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useRoleGet(id || '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Role | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'isSysAdminRole') return;
          if (key === 'permissions') {
            // add all permissions to the detail items
            Object.entries(value as Permissions).forEach(([module, permissions]) => {
              detailItems.push({
                label: t(`modules.${module}`),
                value: Object.entries(permissions)
                  .filter(([_, access]) => access === true)
                  .map(([permission, access]) => {
                    return `${t(`roles.permissions.${permission}`)}`;
                  })
                  .join(', '),
              });
            });
            return;
          }
          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: formatDate(value),
            });
            return;
          }

          if (key === 'id') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: value.toString(),
            });
            return;
          }
          detailItems.push({
            label: t(`roles.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('roles.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('roles.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('roles.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('roles.title') })}
        showBackButton={true}
        trailTarget={id}
      />
      <div className="row mt-3">
        <RoleDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(memo(RoleDetailsContainer), 'role.view');
